// Widget bootstrap: install buffers → mount UI → (on first open) fetch config
// + send heartbeat. On submit, collect metadata + buffers and POST to the
// submissions endpoint. After each successful submit, pre-warm the next token
// so the user can file additional reports from the same page without
// refreshing (see Issue #27).
//
// #598 — page load makes ZERO requests to admin.oncatch.app.
//
// Until #598 the widget fetched /widget-config and POSTed /heartbeat on every
// page load. Both routes write an `auth_events` row carrying the visitor's IP
// and user-agent (widget-config/route.ts, the token_issued_attempt rate-limit
// row and the token_issued audit row), so every anonymous visitor to any page
// carrying the widget was recorded in our database before touching anything —
// a processing activity our customers' DPAs have to cover, for people who
// never used the feature.
//
// It also meant the config fetch's retry loop (4 attempts, 8s timeout each,
// plus backoff — api.ts) could hold requests open for ~40s on page load when
// admin.oncatch.app was unreachable, which is enough to break a host's
// Playwright `networkidle` wait. That is what jammed a customer's deploy gate
// for 37 hours on 2026-04-29; it was misdiagnosed at the time as the widget
// holding a long-poll open, which it never has.
//
// Both network calls now happen on the reporter's first open instead. The
// launcher mounts from local state only.

import { ApiClient, generateIdempotencyKey, type WidgetConfigResponse } from './api.ts';
import { AttachmentManager, AttachmentValidationError } from './attachments.ts';
import {
  disableCapture,
  installBuffers,
  snapshotConsole,
  snapshotNetworkErrors,
} from './buffers.ts';
import { detectCredentialPage, watchForCredentialPage } from './credential-page.ts';
import { installErrorHandlers } from './error-reporting.ts';
import { getMessages, resolveLocaleFromBrowser } from './i18n/index.ts';
import { getCurrentIdentity, installRealApi, setWorkspaceFlags } from './identity.ts';
import { captureMetadata } from './metadata.ts';
import { mark, measure, WidgetMark } from './perf.ts';
import { compileRedactPatterns, redactStringValues } from './privacy-mask.ts';
import { mountUi, type UiHandlers } from './ui.ts';

// Vite injects the widget version from packages/widget/package.json at build
// time via the IIFE entry. The fallback string only fires under the dev
// `/src/index.ts` path where the constant isn't replaced; it's still useful
// to surface "unknown" in Sentry tags rather than a build error.
declare const __WIDGET_VERSION__: string | undefined;

// #503 — cache the (non-token) enabled config so a return visit paints the
// widget instantly while we revalidate in the background. NEVER cache the auth
// token (a credential); auth self-heals via api.refreshToken() on first use.
// A 30-min TTL bounds how stale the cached privacy settings can be.
const CONFIG_CACHE_PREFIX = 'oncatch_cfg_';
const CONFIG_CACHE_TTL_MS = 30 * 60 * 1000;

function readCachedConfig(workspaceId: string): WidgetConfigResponse | null {
  try {
    const raw = localStorage.getItem(CONFIG_CACHE_PREFIX + workspaceId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { config?: WidgetConfigResponse; cachedAt?: number };
    if (!parsed?.config?.enabled || typeof parsed.cachedAt !== 'number') return null;
    if (Date.now() - parsed.cachedAt > CONFIG_CACHE_TTL_MS) return null;
    return parsed.config;
  } catch {
    return null;
  }
}

function writeCachedConfig(workspaceId: string, config: WidgetConfigResponse): void {
  if (!config.enabled) return;
  try {
    // Strip the token + its expiry — JSON.stringify drops the undefined keys, so
    // the persisted blob never contains a credential.
    const safe: WidgetConfigResponse = { ...config, token: undefined, tokenExpiresAt: undefined };
    localStorage.setItem(
      CONFIG_CACHE_PREFIX + workspaceId,
      JSON.stringify({ config: safe, cachedAt: Date.now() }),
    );
  } catch {
    // storage unavailable / quota — skip caching (purely an optimization).
  }
}

function clearCachedConfig(workspaceId: string): void {
  try {
    localStorage.removeItem(CONFIG_CACHE_PREFIX + workspaceId);
  } catch {
    // ignore — nothing to clear / storage blocked.
  }
}

/**
 * #598 — returns the mounted UI (previously `void`; the value was built and
 * discarded). Nothing in the production path consumes it — index.ts still
 * fires-and-forgets — but returning it lets a caller drive open()/close()
 * without reaching through the CLOSED shadow root, which is the only way to
 * exercise the deferred first-open path.
 */
export async function bootstrap(
  workspaceId: string,
  apiBaseUrl: string,
  scriptLocale: string | null = null,
  widgetBaseUrl = typeof location === 'undefined' ? '' : location.origin,
  assistantEndpoint?: string,
): Promise<ReturnType<typeof mountUi> | null> {
  // #341 — mark the start of bootstrap so the whole boot phase (and the
  // config-fetch span within it) is measurable from the browser performance
  // timeline. Measurement only.
  mark(WidgetMark.bootstrapStart);

  // #599 — credential-page suppression. FIRST statement with any effect, and
  // deliberately so: this runs before installBuffers() patches console / fetch
  // / XMLHttpRequest on the host page, and before anything touches the network.
  // On a credential page the widget therefore leaves NO trace at all — no
  // patched globals, no listeners, no DOM node, no request.
  //
  // The check is a synchronous DOM query, so ordering it first costs nothing.
  const credentialPage = detectCredentialPage();
  if (credentialPage.isCredentialPage) {
    // Local-only diagnostic. There is deliberately NO network beacon here:
    // #598 exists precisely to stop the widget contacting our API on page load,
    // and a suppression ping would reinstate exactly that — an unsolicited
    // request carrying the visitor's IP from every credential-page view, which
    // is the harm this pair of issues removes. An integrator debugging "why is
    // the widget missing on this page" finds the answer in their own devtools
    // console, which is where they are already looking.
    // biome-ignore lint/suspicious/noConsole: widget boot diagnostic; the only way to surface at load time
    console.info(
      '[OnCatch] widget suppressed on this page: a password field is present, ' +
        'so it is treated as a credential-entry page. This is intentional — see ' +
        'https://github.com/onpathtesting/oncatch-app/issues/599',
    );
    return null;
  }

  // Install ring buffers as early as possible so we catch the most recent console + network.
  installBuffers();

  const api = new ApiClient(apiBaseUrl);
  api.bindWorkspace(workspaceId);
  // #263: read the browser's Global Privacy Control signal BEFORE the first
  // server request so widget-config / submissions / heartbeat all carry the
  // signal as compliance evidence. The browser also sends `Sec-GPC: 1` as
  // an HTTP header natively — server reads both as defense-in-depth.
  // navigator.globalPrivacyControl is the JS API spec'd by the GPC
  // specification (https://globalprivacycontrol.org). Property is undefined
  // on browsers that don't implement it; we guard with a Boolean coerce.
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const gpcSignal = Boolean(
    (nav as Navigator & { globalPrivacyControl?: boolean })?.globalPrivacyControl,
  );
  api.setGpc(gpcSignal);

  // #503 — a cached enabled-config from a previous visit lets the panel render
  // its real form immediately on first open instead of the pending state. Read
  // from localStorage only; no network. The cache carries NO token (never
  // persist a credential) — auth self-heals via the first-open fetch below.
  const cached = readCachedConfig(workspaceId);

  // #598 — the config the widget is currently operating under. Starts as the
  // cached one (or null) and is replaced by the authoritative one on first
  // open. The submit path reads redaction patterns through this, so it can
  // never redact with the mount-time placeholder after the real config lands.
  let activeConfig: WidgetConfigResponse | null = cached;

  // #629 — has the first-open config resolution finished successfully?
  //
  // Gates the panel-open token prewarm below. Until this flips, the prewarm
  // would fire a SECOND /widget-config in parallel with the one
  // resolveConfigOnFirstOpen is already running for the same open — see the
  // comment at the onOpen handler for why the existing dedup does not catch it.
  let firstOpenResolved = false;

  // #598 — the config fetch that used to gate the mount. Now invoked by the UI
  // on the reporter's FIRST OPEN (mountUi's onFirstOpen), so page load is
  // network-silent. Returns the patch the UI merges into its live handlers.
  //
  // Everything that previously happened at load — token, heartbeat,
  // sensitive-data-mode enforcement, cache write — happens here instead, in
  // the same order, so the post-open state is identical to the pre-#598
  // post-load state.
  const resolveConfigOnFirstOpen = async (): Promise<
    { ok: true; patch?: Partial<UiHandlers> } | { ok: false; reason: 'disabled' | 'offline' }
  > => {
    mark(WidgetMark.configFetchStart);
    let fresh: WidgetConfigResponse;
    try {
      fresh = await api.fetchConfig(workspaceId, 4);
    } catch {
      // Network failure — transient and retryable. The UI keeps the widget up
      // and re-runs this on the next open, so there is nothing to arm here
      // (the pre-#598 'online' re-bootstrap listener is gone with the
      // load-time fetch it existed to retry).
      return { ok: false, reason: 'offline' };
    } finally {
      mark(WidgetMark.configFetchEnd);
      measure('config-fetch', WidgetMark.configFetchStart, WidgetMark.configFetchEnd);
    }

    if (!fresh.enabled || !fresh.token) {
      // Workspace disabled, origin not allowlisted, rate limited, or unknown.
      // Pre-#598 this produced a console warning and no widget at all; the
      // panel now says so instead (renderUnavailable in ui.ts).
      clearCachedConfig(workspaceId);
      return { ok: false, reason: 'disabled' };
    }

    // Privacy safety net, unchanged in intent from #503: sensitive-data-mode
    // may have been switched ON since the cached visit, and buffers have been
    // capturing since page load either way. Drop everything collected so far.
    if (fresh.sensitiveDataMode) disableCapture();

    activeConfig = fresh;
    // #629 — set BEFORE the heartbeat below so that any open racing this one
    // sees a resolved state rather than re-entering the duplicate-fetch path.
    firstOpenResolved = true;
    api.setToken(fresh.token, fresh.tokenExpiresAt);
    // Heartbeat moves here from page load. It now counts ENGAGED visitors
    // rather than pageviews — a deliberate semantic change (see #598), and the
    // reason no anonymous visitor reaches auth_events any more.
    api.heartbeat(workspaceId).catch(() => undefined);
    writeCachedConfig(workspaceId, fresh);

    setWorkspaceFlags({
      identificationEnabled: Boolean(fresh.identificationEnabled),
      sensitiveDataMode: Boolean(fresh.sensitiveDataMode),
    });

    return { ok: true, patch: uiPatchFromConfig(fresh) };
  };

  const mounted = mountWidget(
    api,
    cached,
    workspaceId,
    scriptLocale,
    resolveConfigOnFirstOpen,
    () => activeConfig,
    () => firstOpenResolved,
    widgetBaseUrl,
    apiBaseUrl,
    assistantEndpoint,
  );

  // #599 — SPA guard. Client-side navigation into a credential route does not
  // re-run bootstrap, so without this the load-time check alone would leave the
  // widget live on /login in every single-page app. Tears down unconditionally
  // (see watchForCredentialPage for why an open panel is not an exemption).
  //
  // disableCapture() before destroy(): the ring buffers have been recording
  // console + network since page load, and once we have decided this is a
  // credential page we should not still be holding what they captured on the
  // way in. Removing the DOM node without clearing them would leave that data
  // in memory for the rest of the page's life.
  watchForCredentialPage(() => {
    disableCapture();
    mounted?.destroy();
    // biome-ignore lint/suspicious/noConsole: widget diagnostic; mirrors the load-time message
    console.info(
      '[OnCatch] widget removed: a password field appeared, so this is now ' +
        'treated as a credential-entry page. This is intentional — see ' +
        'https://github.com/onpathtesting/oncatch-app/issues/599',
    );
  });

  // #341 — boot span now ends at the network-free mount, which is the whole
  // point of #598: cold-start no longer includes a config round trip.
  mark(WidgetMark.bootstrapEnd);
  measure('bootstrap', WidgetMark.bootstrapStart, WidgetMark.bootstrapEnd);
  return mounted;
}

/**
 * #598 — the subset of UiHandlers that /widget-config determines. Extracted so
 * the first-open patch and the initial mount build the same shape from the
 * same source, rather than drifting apart.
 */
function uiPatchFromConfig(config: WidgetConfigResponse): Partial<UiHandlers> {
  return {
    sensitiveDataMode: config.sensitiveDataMode ?? false,
    storageState: config.storageState ?? 'normal',
    // #648 — notify-on-fix checkbox gate rides the same patch so the deferred
    // first-open config flip (#598) carries it into the form render.
    fixNotificationsEnabled: Boolean(config.fixNotificationsEnabled),
    dictationEnabled: config.dictationEnabled === true,
    privacyConfig: config.privacyConfig,
    workspaceName: config.workspaceName,
    attachmentRetentionDays: config.attachmentRetentionDays,
  };
}

// #492: extracted from bootstrap so the config fetch can guard + retry around it
// without re-running the one-time setup.
//
// #598: `config` is now the CACHED config or null — never a freshly-fetched
// one, because no fetch has happened yet at mount time. The real config
// arrives through `onFirstOpen` and is merged into the live handlers by
// mountUi's applyConfig. Called exactly once per page load.
function mountWidget(
  api: ApiClient,
  config: WidgetConfigResponse | null,
  workspaceId: string,
  scriptLocale: string | null,
  onFirstOpen: NonNullable<UiHandlers['onFirstOpen']>,
  getActiveConfig: () => WidgetConfigResponse | null,
  /** #629 — true once resolveConfigOnFirstOpen has succeeded. Gates the prewarm. */
  hasFirstOpenResolved: () => boolean,
  widgetBaseUrl: string,
  apiBaseUrl: string,
  assistantEndpoint?: string,
): ReturnType<typeof mountUi> | null {
  // #503 — a token is ABSENT here by construction: the cache never stores one
  // (writeCachedConfig strips it) and #598 removed the load-time fetch that
  // used to supply one. Auth self-heals — onFirstOpen sets a real token before
  // the form is usable, and submit/upload call api.refreshToken() if it lapses.

  // Sensitive-data-mode: when a cached config says the workspace has it on,
  // short-circuit the ring buffers immediately rather than waiting for the
  // first open. A workspace that turned it ON since the cached visit is caught
  // by the same call in resolveConfigOnFirstOpen.
  if (config?.sensitiveDataMode) {
    disableCapture();
  }

  // #212 PR-B — wire the identity module to the workspace's identification
  // flags + drain any pre-bootstrap OnCatch.identify() / .reset() calls
  // that the install snippet queued into window.OnCatch._q.
  //
  // #598: seeded from cache (or conservative defaults) at mount and re-applied
  // authoritatively in resolveConfigOnFirstOpen. Defaulting
  // identificationEnabled to FALSE when uncached is the safe direction — an
  // identify() payload is queued, not dropped, and only starts being honoured
  // once the server confirms the workspace allows it.
  setWorkspaceFlags({
    identificationEnabled: Boolean(config?.identificationEnabled),
    sensitiveDataMode: Boolean(config?.sensitiveDataMode),
  });
  installRealApi();

  // #34 — install global error/unhandledrejection handlers. Tiny module
  // (~700 bytes minified) that POSTs widget-frame errors to the
  // admin-relay endpoint where server-side Sentry captures them. Source-
  // frame filter ensures host-page bugs and ad scripts are ignored.
  //
  // #598: submitWidgetError early-returns without a token, so widget errors
  // raised before the first open are dropped rather than queued. Accepted:
  // relaying them would mean a network call from a visitor who never engaged,
  // which is the exact thing this issue removes.
  installErrorHandlers({
    api,
    workspaceId,
    widgetVersion: typeof __WIDGET_VERSION__ === 'string' ? __WIDGET_VERSION__ : 'unknown',
  });

  // #161 (#5a): one AttachmentManager per widget mount, shared across the
  // form's lifetime. Slot state survives close→reopen UNTIL submission
  // succeeds (then reset()) or the user explicitly removes a slot.
  // releaseAllPending fires on close so abandoned reservations don't sit
  // on the workspace cap for the full 5-min TTL.
  const attachmentManager = new AttachmentManager(api, {
    workspaceId,
    backgroundAttachmentsEnabled: () => getActiveConfig()?.backgroundAttachmentsEnabled === true,
    uploadPolicy: () => getActiveConfig()?.uploadPolicy,
    sourceTextUploadsEnabled: () => getActiveConfig()?.sourceTextUploadsEnabled === true,
  });

  // Forward-declare so onSubmit can reference the mounted UI for challenge display.
  // Populated after mountUi below. Widgets always mount synchronously today so
  // the only way ui could be null is if an onSubmit somehow fired before mount —
  // not a real case.
  let ui: ReturnType<typeof mountUi> | null = null;

  // #217: compile redaction patterns so each submission doesn't pay the
  // regex-compilation cost. Defaults always apply; workspace arrays from
  // /widget-config add to them.
  //
  // #598: the workspace's extra patterns are not known at mount (no fetch has
  // happened). They arrive on first open, so this is memoised on the config
  // object's identity rather than computed once — recompiled exactly once when
  // the real config replaces the cached one, and reused on every submit after.
  // Reading through `getActiveConfig()` is what keeps a submission from ever
  // redacting with stale patterns.
  let patternCacheKey: WidgetConfigResponse | null | undefined;
  let patternCacheValue: RegExp[] = [];
  const getRedactPatterns = (): RegExp[] => {
    const active = getActiveConfig();
    if (patternCacheKey !== active) {
      patternCacheKey = active;
      patternCacheValue = compileRedactPatterns(active?.privacyConfig?.redactPatterns);
    }
    return patternCacheValue;
  };

  // #56: resolve the active locale once at mount. Priority:
  //   1. Script-tag attribute (`data-locale`) passed through index.ts
  //   2. `navigator.languages` / `navigator.language`
  //   3. Hard fallback to `en`
  //
  // #598: the server-side per-workspace override that used to head this list
  // is not available at mount. It is also not yet real — /widget-config
  // hardcodes `locale: null` pending the `workspace.widget_locale` column (see
  // the comment in widget-config/route.ts). When that column ships, the
  // override must be applied at MOUNT (i.e. persisted through the config
  // cache), not via the first-open patch: `locale` drives the shadow host's
  // `lang` attribute and every rendered string, so switching it mid-session
  // would re-render the panel under the reporter.
  const locale = resolveLocaleFromBrowser(scriptLocale);
  const messages = getMessages(locale);

  ui = mountUi({
    assistantEndpoint,
    attachmentManager,
    // #56: locale + messages drive every reporter-facing string + the
    // shadow-host `lang` attribute + Turnstile's data-language override.
    locale,
    messages,
    // #598: these five come from the cached config when there is one, and are
    // otherwise conservative defaults that hold only until the first open
    // resolves the real config into them via applyConfig.
    //
    // `sensitiveDataMode: true` is the deliberate uncached default. It is the
    // privacy-safe direction — it suppresses the recording button and the
    // contact-prefill checkbox until the server confirms the workspace allows
    // them. Getting this backwards would show a Record button to a workspace
    // that had explicitly opted out of exactly that.
    sensitiveDataMode: config ? (config.sensitiveDataMode ?? false) : true,
    // #136: cap-state hint. 'normal' uncached — the real state lands before
    // the form renders, so a full workspace never gets usable capture buttons.
    storageState: config?.storageState ?? 'normal',
    // #648: notify-on-fix checkbox gate. FALSE is the safe uncached default —
    // the checkbox only appears once the server confirms the workspace enabled
    // reporter fix notifications (mirrors identificationEnabled's direction).
    fixNotificationsEnabled: Boolean(config?.fixNotificationsEnabled),
    dictationEnabled: config?.dictationEnabled === true,
    transcribeDraft: (blob, attemptId, signal) => api.transcribeDraft(blob, attemptId, signal),
    // #217: privacy masking config — widget threads maskSelectors into the
    // screenshot capture path; bootstrap (here) consumes redactPatterns
    // when building the submission payload below.
    privacyConfig: config?.privacyConfig,
    // #241: workspace context from /widget-config so the recording-flow
    // consent modal can show the actual workspace name + retention period.
    workspaceName: config?.workspaceName,
    attachmentRetentionDays: config?.attachmentRetentionDays,
    widgetBaseUrl,
    apiBaseUrl: apiBaseUrl,
    getHostedRecorderToken: () => api.getFreshToken(),
    // #598: defer the form render (and therefore the config fetch) until the
    // reporter actually opens the widget.
    deferInitialRender: true,
    onFirstOpen,
    // Keys the opt-in reporter-contact prefill so two widgets on one origin
    // never share saved contact details.
    workspaceId,
    // #163 PR C: best-effort widget audit-event ingest. Recording-flow fires
    // these at consent_accepted / started / stopped; api.submitWidgetEvent
    // forwards to /api/v1/widget-events which logs to auth_events.
    onWidgetEvent: (event) => {
      void api.submitWidgetEvent({
        workspaceId,
        eventType: event.type,
        metadata: event.metadata,
      });
    },
    onOpen: () => {
      // #629 — do NOT prewarm before the first open has resolved.
      //
      // On the FIRST open there is no token yet (#598 deferred the load-time
      // fetch), so prewarmTokenIfStale() sees a stale token and fires
      // refreshToken() -> fetchConfig(). Meanwhile ensureReady() is already
      // running resolveConfigOnFirstOpen(), which calls fetchConfig() directly.
      // Two parallel /widget-config requests for one open.
      //
      // ApiClient's refreshInFlight guard does not catch this: it dedupes calls
      // to refreshToken(), and the first-open path calls fetchConfig()
      // directly, so the two never meet. Pre-#598 the situation could not arise
      // because a token already existed by the time the panel could be opened,
      // making this a no-op.
      //
      // Measured in production 2026-08-11: two widget-config requests per first
      // open, at 1.1-3.3s each (see #607), and two of the reporter's ten
      // per-minute token attempts spent instead of one.
      if (!hasFirstOpenResolved()) return;

      // #533 — pull the token refresh forward to panel-open so it hides behind
      // the time the reporter spends typing instead of landing in front of the
      // submission POST. No-op when the token is already fresh (return visits
      // inside the 15-min TTL cost zero requests); deduped by refreshInFlight,
      // so an immediate submit awaits the same in-flight refresh.
      // form_start analytics could hook in here later (v1.5).
      api.prewarmTokenIfStale();
    },
    onClose: () => {
      // #416 — close/reopen on the same page preserves attachment slots
      // and the server reservations behind them. No release, no reset.
      // Page navigation or refresh destroys all JS state for free, and
      // any reservations the user truly abandons get reclaimed by the
      // existing retention cron after TTL — same cleanup path that was
      // already covering "closed but never reopened" cases. Post-submit
      // reset is unchanged (lives in the onSubmit success branch).
    },
    onSubmit: async (form) => {
      const metadata = captureMetadata();

      // #161: prepare attachments (re-reserve any TTL-expired slots).
      // The UI's submit-blocker modal handles the has-failed case before
      // we get here, so this should only throw on TTL re-reserve failure.
      let attachments: Array<{ reservationId: string }> = [];
      try {
        attachments = await attachmentManager.prepareForSubmit();
      } catch (err) {
        if (err instanceof AttachmentValidationError) {
          if (err.reason === 'attachment_credentials_changed') {
            return { ok: false, error: 'attachment_credentials_changed' };
          }
          if (err.reason === 'ttl_reserve_failed') {
            return { ok: false, error: 'attachment_ttl_failed' };
          }
          if (err.reason === 'has_failed_slots') {
            // UI should have prevented this, but defensive: surface an error
            // rather than dropping the user's submission silently.
            return { ok: false, error: 'attachment_failed' };
          }
        }
        return { ok: false, error: 'attachment_failed' };
      }

      // #212 PR-B — attach the current identity if any. Server-side gates
      // (sensitive_data_mode, identification_enabled toggle, validation)
      // decide whether to actually store; widget always sends whatever
      // host app called OnCatch.identify() with.
      const identity = getCurrentIdentity();

      const payload = {
        workspaceId,
        idempotencyKey: generateIdempotencyKey(),
        title: form.title,
        summary: form.summary,
        reporterName: form.reporterName || null,
        reporterEmail: form.reporterEmail || null,
        // #648 — only sent when true (additive; stale servers ignore it).
        ...(form.notifyOnFix ? { notifyOnFix: true } : {}),
        metadataJson: metadata as unknown as Record<string, unknown>,
        // #217: regex-redact any PII shaped strings (CC numbers, emails,
        // SSNs, customer-configured patterns) BEFORE the snapshot leaves
        // the browser. Defaults always apply even when the workspace has
        // no extras configured.
        // #598: resolved at submit time (memoised) so a submission always
        // redacts with the workspace's real patterns, never the mount-time
        // placeholder set.
        consoleLogJson: redactStringValues(snapshotConsole(), getRedactPatterns()),
        networkErrorsJson: redactStringValues(snapshotNetworkErrors(), getRedactPatterns()),
        ...(attachmentManager.usesBackgroundProcessing()
          ? { attachmentProcessingMode: 'background' as const }
          : {}),
        ...(attachments.length > 0 ? { attachments } : {}),
        ...(attachmentManager.usesBackgroundProcessing() &&
        attachmentManager.failedAttachmentDescriptors().length
          ? { failedAttachments: attachmentManager.failedAttachmentDescriptors() }
          : {}),
        ...(identity !== null ? { identifiedReporter: identity } : {}),
        // #215 — pin-to-element capture data, present when the submission
        // was filed via pin mode. Server-side Zod validates the shape and
        // the JSONB CHECK constraint guards against malformed inserts.
        ...(form.pinned !== undefined ? { pinned: form.pinned } : {}),
      };

      let credentialRepairs = 0;
      const submitPrepared = async () => {
        let result = await api.submit(payload, {
          attachmentCredentialGeneration: attachmentManager.preparedCredentialGeneration(),
        });
        if (result.error === 'attachment_credentials_changed' && credentialRepairs++ === 0) {
          try {
            payload.attachments = await attachmentManager.prepareForSubmit();
            payload.failedAttachments = attachmentManager.failedAttachmentDescriptors();
          } catch {
            return { ok: false, error: 'attachment_credentials_changed' };
          }
          result = await api.submit(payload, {
            attachmentCredentialGeneration: attachmentManager.preparedCredentialGeneration(),
          });
        }
        return result;
      };
      const result = await submitPrepared();

      if (!result.ok || !result.referenceCode) {
        return { ok: false, error: result.error ?? 'unknown_error' };
      }
      // Pre-warm the next token in the background so "Report another" (or the
      // user re-opening the widget later) has a fresh token ready to go.
      api.prewarmNextToken();
      // #161: clear slot state on success so the next form starts empty.
      const failedLocal = attachmentManager.failedAttachmentDescriptors().length;
      attachmentManager.reset();
      // #256: count attachments the server's reconcile loop dropped, so the
      // UI can render a partial-success notice instead of the cheerful all-
      // green success state. Stale clients without the new field skip this.
      const attachmentResults = result.attachmentResults ?? [];
      const attachmentsTotal = attachmentResults.length + failedLocal;
      const attachmentsFailed =
        attachmentResults.filter((r) => r.status === 'failed').length + failedLocal;
      return {
        ok: true,
        referenceCode: result.referenceCode,
        attachmentsFailed,
        attachmentsTotal,
      };
    },
  });

  return ui;
}
